/**
 * #538 (trial) — the identity capsule of a PDApp window in the drive / workspace, drawn by the SPA
 * itself (not the accounts frame): it floats at the bottom centre over the app, in the SPA's own
 * theme, and hides into a round button tucked mostly below the SCREEN's bottom edge (centred under
 * the app's window), which brings it back.
 *
 * It shows what the PDApp is told about the person, as the pd.app bar does: the name only when the
 * PDApp may see it, the email in full only when it may, masked otherwise (the same mask as the bar
 * and the server's actor hint). The identity opens the SPA's consent dialog; Share and the star are
 * the window's own Share and Pin; (i) shows the PDApp and who made it, as the splash does.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, MiniappIcon, useToastOptional } from '@papan/core-ui/ui'
import { usePdappHistory, pinActiveApp, setEntryPinned } from '@papan/files/ui'
import { useQueryClient } from '@tanstack/react-query'
import { avatarGradient, avatarGradientForHue, avatarInitials, type MiniappIconSpec } from '@papan/types'
import { useBrand } from '@papan/brand/react'
import { useTranslation } from '@papan/i18n'
import { ChevronDown, Share, Star, Flag, Info, List, ArrowUpRight } from 'lucide-react'
import { getActiveOrgEmail, getApiBase, sessionFetch } from '@papan/api-client'
import { fetchConsentRequirement } from '../client/consent-requirement.js'

/** Must match the pd.app bar's `maskEmail` (apps/web/public/__accounts/embed.html) and the
 *  server's (features/files/api/preview-serve.ts). */
function maskEmail(e: string): string {
  const p = e.split('@')
  if (p.length !== 2 || !p[0] || !p[1]) return 'd***'
  const dot = p[1].lastIndexOf('.')
  return p[0][0] + '***@' + p[1][0] + '***' + (dot >= 0 ? p[1].slice(dot) : '')
}

const HIDDEN_KEY = 'papan_capsule_hidden'
function readHidden(): boolean {
  try { return localStorage.getItem(HIDDEN_KEY) === '1' } catch { return false }
}
function writeHidden(on: boolean): void {
  try { localStorage.setItem(HIDDEN_KEY, on ? '1' : '') } catch { /* private mode */ }
  // Every open PDApp window follows (the choice is the browser's, not one window's).
  try { window.dispatchEvent(new CustomEvent(HIDDEN_EVENT, { detail: on })) } catch { /* no window */ }
}
const HIDDEN_EVENT = 'papan:capsule-hidden'

export interface PdappCapsuleProps {
  /** The requirement endpoint for this app (what it asks for, and what is on record). */
  requirementUrl: string
  /** Bumped after a consent decision, to re-read what the PDApp may see. */
  refreshKey: number
  onPermissions: () => void
  onShare?: () => void
  /** "Made by" → show this PDApp in its folder in the drive (the window's Reveal in drive). */
  onLocate?: () => void
  /** Open the PDApps list (favorites + recent) in the CURRENT org — the SPA's own address prefix, never a hand-made `/~`. */
  onOpenList?: () => void
  /** A PDApp (Mode B) — its info shows the PDAPP chip; a standalone page (Mode A) or unknown shows none. */
  isPdapp?: boolean
  /** Is the window on screen? Nothing is fetched for a window in the background. Default true. */
  active?: boolean
  /** The PDApp (its folder) for the star: favorites are the PDApps page's Pinned list — the same
   *  list a pd.app link's star adds to. Omitted ⇒ no star. */
  favoriteAppId?: string
  /** The PDApp's icon, as its window draws it. */
  icon?: { spec: MiniappIconSpec | null; rev: string | null; refId: string }
}

export function PdappCapsule({ requirementUrl, refreshKey, onPermissions, onShare, onLocate, onOpenList, isPdapp, active = true, favoriteAppId, icon }: PdappCapsuleProps) {
  const { productName } = useBrand()
  const { t } = useTranslation('files')
  // The round button is portaled outside every ThemedArea: it carries the SPA's theme class itself.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [hidden, setHidden] = useState(readHidden)
  const [who, setWho] = useState<{ name: string | null; email: string | null }>({ name: null, email: null })
  const [granted, setGranted] = useState<string[]>([])
  const [pop, setPop] = useState<'info' | 'report' | null>(null)
  const [app, setApp] = useState<{ name: string | null; owner: string | null; ownerHue: number | null; validated?: boolean }>({ name: null, owner: null, ownerHue: null })
  useEffect(() => {
    const on = (e: Event) => setHidden(!!(e as CustomEvent<boolean>).detail)
    window.addEventListener(HIDDEN_EVENT, on)
    return () => window.removeEventListener(HIDDEN_EVENT, on)
  }, [])
  // The round button lives at the bottom of the SPA (the screen), centred under this window: it is
  // portaled to <body> and follows the window's horizontal centre. A window that isn't showing (a
  // kept-alive deck, display:none) has no width — its button isn't drawn.
  const anchorRef = useRef<HTMLDivElement>(null)
  const [centerX, setCenterX] = useState<number | null>(null)
  useEffect(() => {
    const el = anchorRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      // Only the window on screen draws its button — a kept-alive window behind it is hidden by
      // CSS (display / visibility), and its button would stack invisibly on the same spot.
      const shown = typeof el.checkVisibility === 'function' ? el.checkVisibility({ checkVisibilityCSS: true, opacityProperty: true } as CheckVisibilityOptions) : true
      setCenterX(r.width > 0 && shown ? Math.round(r.left + r.width / 2) : null)
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)
    window.addEventListener('resize', measure)
    const t = window.setInterval(measure, 1000) // a window moved without resizing (a sidebar toggle)
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); window.clearInterval(t) }
  }, [])

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const res = await sessionFetch(`${getApiBase()}/api/auth/get-session`, { credentials: 'include' })
        const body = res.ok ? await res.json() as { user?: { name?: string; email?: string } } | null : null
        if (live) setWho({ name: body?.user?.name ?? null, email: getActiveOrgEmail() || body?.user?.email || null })
      } catch { /* a session that ended shows nothing */ }
    })()
    return () => { live = false }
  }, [])

  useEffect(() => {
    if (!active) return
    let live = true
    void (async () => {
      try {
        const { body } = await fetchConsentRequirement<{ alreadyGranted?: string[] | null; appName?: string; appOwnerName?: string | null; appOwnerHue?: number | null; validated?: boolean }>(requirementUrl)
        if (!live) return
        setGranted(Array.isArray(body?.alreadyGranted) ? body.alreadyGranted : [])
        setApp({ name: body?.appName ?? null, owner: body?.appOwnerName ?? null, ownerHue: typeof body?.appOwnerHue === 'number' ? body.appOwnerHue : null, validated: body?.validated === true })
      } catch { /* unknown → shown masked */ }
    })()
    return () => { live = false }
  }, [requirementUrl, refreshKey, active])

  // Review round 2 — the item's own name, never "PDApp" for a standalone page.
  const itemName = app.name ?? (isPdapp ? t('pdappCapsule.thisPdapp') : t('pdappCapsule.thisPage'))
  // Esc closes an open popover (as the pd.app bar's dialogs do).
  useEffect(() => {
    if (!pop) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPop(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pop])
  const nameOn = granted.includes('name') && !!who.name
  const shownEmail = who.email ? (granted.includes('email') ? who.email : maskEmail(who.email)) : null
  // The person's own initials, as the taskbar shows them (this is the SPA, drawn for them alone).
  const initial = avatarInitials(who.name || who.email || '?')
  const toggle = (on: boolean) => { setHidden(on); writeHidden(on); setPop(null) }
  // The star: the window's Pin, then a toast saying so, with the way to the PDApps page (recent and
  // favorites). The SPA's own toast — the PDApp, in its frame, can neither see nor imitate it.
  const toast = useToastOptional()
  const qc = useQueryClient()
  const history = usePdappHistory(!!favoriteAppId)
  const favEntry = favoriteAppId ? history.data?.pinned.find((e) => e.kind === 'app' && e.app?.appId === favoriteAppId) : undefined
  const isPinned = !!favEntry
  const [favBusy, setFavBusy] = useState(false)
  const togglePin = async () => {
    if (!favoriteAppId || favBusy) return
    const adding = !isPinned
    setFavBusy(true)
    try {
      if (adding) await pinActiveApp(qc, favoriteAppId)
      else if (favEntry) await setEntryPinned(qc, favEntry.key, false)
    } catch {
      toast?.error(adding ? t('pdappCapsule.favoriteAddFailed') : t('pdappCapsule.favoriteRemoveFailed'))
      return
    } finally { setFavBusy(false) }
    toast?.success(adding ? t('pdappCapsule.favoriteAdded') : t('pdappCapsule.favoriteRemoved'), 5000, onOpenList ? {
      label: t('pdappCapsule.viewList'),
      onClick: openPdappList,
    } : undefined)
  }
  // The PDApps page (favorites and recent), in this org — the host's own navigation.
  function openPdappList() { onOpenList?.() }

  const iconBtn = 'inline-flex items-center justify-center w-[30px] h-[30px] rounded-full themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors'
  const avatar = (size: number) => (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42, background: avatarGradient(who.email ?? '') }}
    >{initial}</span>
  )

  return (
    <div ref={anchorRef} className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center" style={{ zIndex: 40, height: 0 }}>
      {/* The capsule. */}
      <div
        role="toolbar"
        aria-label={t('pdappCapsule.toolbar', { name: itemName })}
        // Hidden, it leaves the tab order and the accessibility tree too (not just the screen).
        inert={hidden}
        className="pointer-events-auto absolute bottom-1.5 flex items-center gap-0.5 h-9 px-[3px] rounded-full border themed-border themed-bg-secondary shadow-lg text-xs"
        style={{
          transition: 'transform .26s cubic-bezier(0.3,0,0,1), opacity .2s ease',
          transform: hidden ? 'translateY(24px) scale(.7)' : 'none',
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? 'none' : 'auto',
        }}
      >
        <button type="button" className={iconBtn} onClick={() => toggle(true)} title={t('pdappCapsule.hide')} aria-label={t('pdappCapsule.hide')}><ChevronDown size={16} /></button>
        <button
          type="button"
          onClick={onPermissions}
          title={t('pdappCapsule.identity')}
          className="inline-flex items-center gap-1.5 h-[30px] pl-0.5 pr-2.5 rounded-full themed-bg-tertiary themed-text max-w-[260px]"
        >
          {avatar(22)}
          {nameOn && <span className="font-medium whitespace-nowrap">{who.name}</span>}
          {shownEmail && <span className="themed-text-muted truncate">{nameOn ? `<${shownEmail}>` : shownEmail}</span>}
        </button>
        <span className="w-px h-5 mx-0.5 themed-bg-tertiary" aria-hidden="true" />
        {onShare && <button type="button" className={iconBtn} onClick={onShare} title={t('pdappCapsule.share')} aria-label={t('pdappCapsule.shareThis', { name: itemName })}><Share size={16} /></button>}
        {favoriteAppId && (
          <button type="button" className={iconBtn} onClick={() => void togglePin()} disabled={favBusy} title={isPinned ? t('pdappCapsule.removeFavorite') : t('pdappCapsule.addFavorite')} aria-label={isPinned ? t('pdappCapsule.removeFavorite') : t('pdappCapsule.addFavorite')} aria-pressed={!!isPinned}>
            <Star size={16} style={isPinned ? { color: '#f59e0b', fill: '#f59e0b' } : undefined} />
          </button>
        )}
        {onOpenList && <button type="button" className={iconBtn} onClick={openPdappList} title={t('pdappCapsule.yourPdappsHint')} aria-label={t('pdappCapsule.yourPdapps')}><List size={16} /></button>}
        <button type="button" className={iconBtn} onClick={() => setPop((v) => v === 'report' ? null : 'report')} title={t('pdappCapsule.report')} aria-label={t('pdappCapsule.reportThis', { name: itemName })}><Flag size={16} /></button>
        <button type="button" className={iconBtn} onClick={() => setPop((v) => v === 'info' ? null : 'info')} title={t('pdappCapsule.about', { name: itemName })} aria-label={t('pdappCapsule.about', { name: itemName })} aria-expanded={pop === 'info'}><Info size={16} /></button>
        {pop === 'report' && (
          <div role="dialog" aria-label={t('pdappCapsule.reportThis', { name: itemName })} className="ws-anim-modal-in origin-bottom-right absolute bottom-11 right-0 w-72 p-3 rounded-xl border themed-border themed-bg-secondary shadow-xl text-xs themed-text">
            <p className="font-semibold mb-1">{t('pdappCapsule.reportThis', { name: itemName })}</p>
            <p className="themed-text-muted mb-2">{t('pdappCapsule.reportSoon')}</p>
            <div className="flex justify-end"><button type="button" className="px-3 py-1 rounded-md themed-bg-tertiary" onClick={() => setPop(null)}>{t('pdappCapsule.ok')}</button></div>
          </div>
        )}
        {pop === 'info' && (
          <div role="dialog" aria-label={t('pdappCapsule.about', { name: itemName })} className="ws-anim-modal-in origin-bottom-right absolute bottom-11 right-0 w-80 p-3.5 rounded-2xl border themed-border themed-bg-secondary shadow-xl text-xs themed-text">
            {/* As the splash shows it: the PDApp, then who made it. */}
            <div className="flex items-center gap-3">
              {icon
                ? <MiniappIcon spec={icon.spec} name={app.name ?? ''} size={44} refId={icon.refId} rev={icon.rev} />
                : <span className="w-11 h-11 rounded-xl themed-bg-tertiary shrink-0" aria-hidden="true" />}
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{itemName}</p>
                {isPdapp && <span className="inline-block mt-1 px-1.5 py-px rounded text-[10px] font-bold tracking-wider" style={{ color: '#14b8a6', background: 'rgba(20,184,166,.16)' }}>PDAPP</span>}
              </div>
            </div>
            <div className="mt-3 p-3 rounded-xl border themed-border">
              <p className="text-[10px] font-semibold uppercase tracking-wider themed-text-muted">{t('pdappCapsule.madeBy')}</p>
              {(() => {
                const who = (
                  <>
                    <span aria-hidden="true" className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-semibold shrink-0" style={{ background: app.ownerHue !== null ? avatarGradientForHue(app.ownerHue) : avatarGradient(app.owner ?? '') }}>
                      {avatarInitials(app.owner || '?')}
                    </span>
                    <span className="text-sm font-medium truncate">{app.owner ?? t('pdappCapsule.someone')}</span>
                  </>
                )
                return onLocate
                  ? (
                    <button type="button" title={t('pdappCapsule.showInDriveHint')} onClick={() => { setPop(null); onLocate() }} className="flex items-center gap-2.5 mt-2 -mx-1.5 p-1.5 w-[calc(100%+0.75rem)] rounded-lg text-left cursor-pointer hover:themed-bg-tertiary">
                      {who}
                      <span className="ml-auto flex items-center gap-1 text-[11px] themed-text-muted shrink-0">{t('pdappCapsule.showInDrive')} <ArrowUpRight size={12} /></span>
                    </button>
                  )
                  : <div className="flex items-center gap-2.5 mt-2">{who}</div>
              })()}
              {/* A validated PDApp (a platform admin marked it): no "is not made by …" — the platform vouched for it. */}
              {!app.validated && <p className="mt-2 themed-text-muted">{t('pdappCapsule.notMadeBy', { name: itemName, brand: productName })}</p>}
            </div>
          </div>
        )}
      </div>
      {/* Hidden: a round button tucked mostly below the screen's bottom edge; it peeks up on hover. */}
      {centerX !== null && typeof document !== 'undefined' && createPortal(
        <button
          type="button"
          onClick={() => toggle(false)}
          title={t('pdappCapsule.show')}
          aria-label={t('pdappCapsule.showBar')}
          // Only while the capsule is hidden is this button reachable.
          inert={!hidden}
          className={`fixed flex justify-center w-11 h-11 rounded-full border themed-border themed-bg-secondary shadow-md pt-[5px] ${themeClass ?? ''}`}
          style={{
            left: centerX - 22,
            bottom: -18,
            zIndex: 60,
            transition: 'transform .26s cubic-bezier(0.3,0,0,1), opacity .2s ease',
            transform: hidden ? 'none' : 'translateY(40px)',
            opacity: hidden ? 1 : 0,
            pointerEvents: hidden ? 'auto' : 'none',
          }}
          onMouseEnter={(e) => { if (hidden) e.currentTarget.style.transform = 'translateY(-6px)' }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = hidden ? 'none' : 'translateY(40px)' }}
        >
          {avatar(22)}
        </button>,
        document.body,
      )}
    </div>
  )
}
